import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, InputGroup, Modal, Pagination, Row, Spinner, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import i18n from "../../../i18n";
import {
    createOrganizationFolder,
    deleteOrganizationFolder,
    fetchDeletedItems,
    fetchGrantTargets,
    fetchOrganizationFolders,
    replaceOrganizationFolderGrants,
    restoreDeletedDocument,
    restoreOrganizationFolder,
    updateOrganizationFolder,
} from "../../../redux/slices/organizationDocumentsSlice";
import "../../permissions/ManagePermissions/ManagePermissions.css";
import "./OrganizationDocuments.css";
import FolderContents from "./FolderContents";

const ITEMS_PER_PAGE = 10;
export const SCOPES = ["employee", "private", "custom"];
export const TARGET_TYPES = ["user", "team", "delegation", "role"];
const PERMISSIONS = ["view", "edit", "manage"];
const TARGET_COLLECTIONS = { user: "users", team: "teams", delegation: "delegations", role: "roles" };

const t = (key, options) => i18n.t(`pages.OrganizationDocuments.${key}`, options);

const emptyForm = (scope) => ({
    name: "",
    description: "",
    scope,
    employee_can_upload: false,
    allow_shared_documents: false,
    grants: [],
});

const GrantsEditor = ({ grants, onChange, grantTargets }) => {
    const [targetType, setTargetType] = useState("team");
    const [targetId, setTargetId] = useState("");
    const [permission, setPermission] = useState("view");

    const options = grantTargets[TARGET_COLLECTIONS[targetType]] || [];
    const available = options.filter(
        (option) => !grants.some((grant) => grant.target_type === targetType && grant.target_id === option.id),
    );

    const addGrant = () => {
        const target = options.find((option) => option.id === targetId);
        if (!target) return;
        onChange([...grants, { target_type: targetType, target_id: target.id, target_name: target.name, permission }]);
        setTargetId("");
    };

    const updatePermission = (index, value) =>
        onChange(grants.map((grant, i) => (i === index ? { ...grant, permission: value } : grant)));

    return (
        <div className="org-documents-grants">
            <Form.Label>{t("access")}</Form.Label>
            <Row className="g-2 align-items-end mb-2">
                <Col xs={12} md={3}>
                    <Form.Select aria-label={t("target_type")} value={targetType} onChange={(e) => { setTargetType(e.target.value); setTargetId(""); }}>
                        {TARGET_TYPES.map((type) => <option key={type} value={type}>{t(`target_${type}`)}</option>)}
                    </Form.Select>
                </Col>
                <Col xs={12} md={5}>
                    <Form.Select aria-label={t("target")} value={targetId} onChange={(e) => setTargetId(e.target.value)}>
                        <option value="">{t("select_target")}</option>
                        {available.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
                    </Form.Select>
                </Col>
                <Col xs={8} md={2}>
                    <Form.Select aria-label={t("permission")} value={permission} onChange={(e) => setPermission(e.target.value)}>
                        {PERMISSIONS.map((level) => <option key={level} value={level}>{t(`permission_${level}`)}</option>)}
                    </Form.Select>
                </Col>
                <Col xs={4} md={2}>
                    <Button variant="outline-secondary" className="w-100" disabled={!targetId} onClick={addGrant}>{t("add")}</Button>
                </Col>
            </Row>
            {grants.length === 0 ? (
                <p className="permissions-empty-state mb-0">{t("no_grants")}</p>
            ) : (
                <ul className="org-documents-grant-list">
                    {grants.map((grant, index) => (
                        <li key={`${grant.target_type}-${grant.target_id}`}>
                            <span className={`org-documents-chip org-documents-chip--${grant.target_type}`}>{t(`target_${grant.target_type}`)}</span>
                            <span className="org-documents-grant-name">{grant.target_name}</span>
                            <Form.Select size="sm" aria-label={t("permission")} value={grant.permission} onChange={(e) => updatePermission(index, e.target.value)}>
                                {PERMISSIONS.map((level) => <option key={level} value={level}>{t(`permission_${level}`)}</option>)}
                            </Form.Select>
                            <button type="button" className="btn-close" aria-label={t("remove")} onClick={() => onChange(grants.filter((_, i) => i !== index))} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const FolderModal = ({ folder, defaultScope, grantTargets, onClose }) => {
    const dispatch = useDispatch();
    const isNew = !folder?.id;
    const [form, setForm] = useState(() => (isNew ? emptyForm(defaultScope) : {
        name: folder.name,
        description: folder.description || "",
        scope: folder.scope,
        employee_can_upload: folder.employee_can_upload,
        allow_shared_documents: folder.allow_shared_documents,
        grants: folder.grants,
    }));
    const [isSaving, setIsSaving] = useState(false);

    const grantsPayload = form.grants.map(({ target_type, target_id, permission }) => ({ target_type, target_id, permission }));

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        try {
            let saved = null;
            if (isNew) {
                saved = await dispatch(createOrganizationFolder({
                    name: form.name,
                    description: form.description || null,
                    scope: form.scope,
                    employee_can_upload: form.employee_can_upload,
                    allow_shared_documents: form.allow_shared_documents,
                    grants: grantsPayload,
                })).unwrap();
            } else {
                await dispatch(updateOrganizationFolder({
                    folderId: folder.id,
                    updates: {
                        name: form.name,
                        description: form.description || null,
                        employee_can_upload: form.employee_can_upload,
                        allow_shared_documents: form.allow_shared_documents,
                    },
                })).unwrap();
                await dispatch(replaceOrganizationFolderGrants({ folderId: folder.id, grants: grantsPayload })).unwrap();
            }
            toast.success(t(isNew ? "created" : "updated"));
            onClose(saved);
        } catch (error) {
            toast.error(typeof error === "string" ? error : t("generic_error"));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show onHide={() => onClose()} size="lg" centered>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>{t(isNew ? "create_folder" : "edit_folder")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-3 mb-3">
                        <Col md={7}>
                            <Form.Group controlId="org-folder-name">
                                <Form.Label>{t("name")}</Form.Label>
                                <Form.Control required maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                            </Form.Group>
                        </Col>
                        <Col md={5}>
                            <Form.Group controlId="org-folder-scope">
                                <Form.Label>{t("scope")}</Form.Label>
                                <Form.Select value={form.scope} disabled={!isNew} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                                    {SCOPES.map((scope) => <option key={scope} value={scope}>{t(`scope_${scope}`)}</option>)}
                                </Form.Select>
                                {!isNew && <Form.Text muted>{t("scope_locked")}</Form.Text>}
                            </Form.Group>
                        </Col>
                    </Row>
                    <p className="permissions-subtitle">{t(`scope_${form.scope}_hint`)}</p>
                    <Form.Group className="mb-3" controlId="org-folder-description">
                        <Form.Label>{t("description")}</Form.Label>
                        <Form.Control as="textarea" rows={2} maxLength={1000} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    </Form.Group>
                    {form.scope === "employee" && (
                        <>
                            <Form.Check
                                className="mb-2"
                                type="switch"
                                id="org-folder-employee-upload"
                                label={t("employee_can_upload")}
                                checked={form.employee_can_upload}
                                onChange={(e) => setForm({ ...form, employee_can_upload: e.target.checked })}
                            />
                            <Form.Check
                                className="mb-3"
                                type="switch"
                                id="org-folder-allow-shared"
                                label={t("allow_shared_documents")}
                                checked={form.allow_shared_documents}
                                onChange={(e) => setForm({ ...form, allow_shared_documents: e.target.checked })}
                            />
                        </>
                    )}
                    <GrantsEditor grants={form.grants} grantTargets={grantTargets} onChange={(grants) => setForm({ ...form, grants })} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => onClose()}>{t("cancel")}</Button>
                    <Button type="submit" className="permissions-cta" disabled={isSaving || !form.name.trim()}>
                        {isSaving ? <Spinner size="sm" /> : t("save")}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

const formatDateTime = (value) => new Date(value).toLocaleString(i18n.language);

const DeletedItemsPanel = () => {
    const dispatch = useDispatch();
    const { deletedItems, deletedLoading } = useSelector((state) => state.organizationDocuments);

    useEffect(() => {
        dispatch(fetchDeletedItems());
    }, [dispatch]);

    const runRestore = async (action) => {
        try {
            await dispatch(action).unwrap();
            toast.success(t("restored"));
            dispatch(fetchDeletedItems());
        } catch (restoreError) {
            toast.error(typeof restoreError === "string" ? restoreError : t("generic_error"));
        }
    };

    if (deletedLoading && deletedItems.folders.length === 0 && deletedItems.documents.length === 0) {
        return (
            <Card className="permissions-panel">
                <Card.Body className="d-flex align-items-center gap-2">
                    <Spinner size="sm" />
                    <span>{t("loading")}</span>
                </Card.Body>
            </Card>
        );
    }

    return (
        <>
            <Card className="permissions-panel mb-3">
                <Card.Header className="bg-body fw-semibold">{t("deleted_folders")}</Card.Header>
                <Card.Body>
                    {deletedItems.folders.length === 0 ? (
                        <p className="permissions-empty-state mb-0">{t("deleted_folders_empty")}</p>
                    ) : (
                        <Table responsive hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>{t("table_name")}</th>
                                    <th>{t("scope")}</th>
                                    <th>{t("table_documents")}</th>
                                    <th>{t("deleted_at")}</th>
                                    <th>{t("deleted_by")}</th>
                                    <th className="text-end">{t("table_actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deletedItems.folders.map((folder) => (
                                    <tr key={folder.id}>
                                        <td className="fw-semibold">{folder.name}</td>
                                        <td>{t(`scope_${folder.scope}`)}</td>
                                        <td>{folder.document_count}</td>
                                        <td>{formatDateTime(folder.deleted_at)}</td>
                                        <td>{folder.deleted_by || "—"}</td>
                                        <td className="text-end">
                                            <button className="btn btn-secondary org-documents-row-btn" onClick={() => runRestore(restoreOrganizationFolder(folder.id))}>
                                                {t("restore")}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            <Card className="permissions-panel">
                <Card.Header className="bg-body fw-semibold">{t("deleted_documents")}</Card.Header>
                <Card.Body>
                    {deletedItems.documents.length === 0 ? (
                        <p className="permissions-empty-state mb-0">{t("deleted_documents_empty")}</p>
                    ) : (
                        <Table responsive hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>{t("table_file")}</th>
                                    <th>{t("table_folder")}</th>
                                    <th>{t("table_employee")}</th>
                                    <th>{t("deleted_at")}</th>
                                    <th>{t("deleted_by")}</th>
                                    <th className="text-end">{t("table_actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deletedItems.documents.map((document) => (
                                    <tr key={document.id}>
                                        <td className="fw-semibold text-break">{document.title}</td>
                                        <td>
                                            {document.folder_name}
                                            {document.folder_deleted && <div><small className="txt-subtle">{t("folder_also_deleted")}</small></div>}
                                        </td>
                                        <td>{document.employee_name || "—"}</td>
                                        <td>{formatDateTime(document.deleted_at)}</td>
                                        <td>{document.deleted_by || "—"}</td>
                                        <td className="text-end">
                                            <button
                                                className="btn btn-secondary org-documents-row-btn"
                                                disabled={document.folder_deleted}
                                                title={document.folder_deleted ? t("restore_folder_first") : undefined}
                                                onClick={() => runRestore(restoreDeletedDocument(document.id))}
                                            >
                                                {t("restore")}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};

const OrganizationDocuments = () => {
    const dispatch = useDispatch();
    const { folders, grantTargets, isLoading, error } = useSelector((state) => state.organizationDocuments);

    const [showDeleted, setShowDeleted] = useState(false);
    const [openFolderId, setOpenFolderId] = useState(null);
    const openFolder = folders.find((folder) => folder.id === openFolderId) || null;
    const [scope, setScope] = useState("employee");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [editing, setEditing] = useState(null);

    useEffect(() => {
        dispatch(fetchOrganizationFolders());
        dispatch(fetchGrantTargets());
    }, [dispatch]);

    useEffect(() => {
        setPage(1);
    }, [scope, search]);

    const filteredFolders = useMemo(() => {
        const query = search.trim().toLowerCase();
        return folders
            .filter((folder) => folder.scope === scope)
            .filter((folder) => !query || folder.name.toLowerCase().includes(query))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [folders, scope, search]);

    const totalPages = Math.max(1, Math.ceil(filteredFolders.length / ITEMS_PER_PAGE));
    const safePage = Math.min(page, totalPages);
    const pagedFolders = filteredFolders.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

    const handleDelete = async (folder) => {
        if (!window.confirm(t("confirm_delete", { name: folder.name }))) return;
        try {
            await dispatch(deleteOrganizationFolder(folder.id)).unwrap();
            toast.success(t("deleted"));
        } catch (deleteError) {
            toast.error(typeof deleteError === "string" ? deleteError : t("generic_error"));
        }
    };

    const accessSummary = (folder) => {
        if (folder.grants.length === 0) {
            return folder.scope === "employee" ? t("access_employee_only") : t("access_admin_only");
        }
        return folder.grants.map((grant) => grant.target_name).join(", ");
    };

    return (
        <Container className="py-4 permissions-shell fade-in-up">
            <div className="permissions-toolbar mb-4">
                <div>
                    <h2 className="permissions-title mb-1">{t("title")}</h2>
                    <p className="permissions-subtitle mb-0">{t("subtitle")}</p>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                    <Button variant="outline-secondary" aria-pressed={showDeleted} onClick={() => { setOpenFolderId(null); setShowDeleted((value) => !value); }}>
                        {t(showDeleted ? "back_to_folders" : "deleted_items")}
                    </Button>
                    {!showDeleted && !openFolder && <Button className="permissions-cta" onClick={() => setEditing("new")}>{t("create_folder")}</Button>}
                </div>
            </div>

            {showDeleted ? <DeletedItemsPanel /> : openFolder ? (
                <FolderContents
                    key={openFolder.id}
                    folder={openFolder}
                    users={grantTargets.users}
                    accessSummary={accessSummary(openFolder)}
                    onBack={() => { setOpenFolderId(null); dispatch(fetchOrganizationFolders()); }}
                />
            ) : (
            <>

            <Card className="permissions-panel mb-3">
                <Card.Body>
                    <Row className="g-2 align-items-end">
                        <Col xs={12} md={6}>
                            <Form.Label htmlFor="org-documents-scope">{t("scope")}</Form.Label>
                            <Form.Select id="org-documents-scope" value={scope} onChange={(e) => setScope(e.target.value)}>
                                {SCOPES.map((value) => <option key={value} value={value}>{t(`scope_${value}`)}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label htmlFor="org-documents-search">{t("search_label")}</Form.Label>
                            <InputGroup>
                                <Form.Control id="org-documents-search" value={search} placeholder={t("search_placeholder")} onChange={(e) => setSearch(e.target.value)} />
                            </InputGroup>
                        </Col>
                    </Row>
                    <p className="permissions-subtitle mt-3 mb-0">{t(`scope_${scope}_hint`)}</p>
                </Card.Body>
            </Card>

            {error && <Alert variant="danger">{typeof error === "string" ? error : t("generic_error")}</Alert>}

            <Card className="permissions-panel">
                <Card.Body>
                    {isLoading ? (
                        <div className="d-flex align-items-center gap-2 py-3">
                            <Spinner size="sm" />
                            <span>{t("loading")}</span>
                        </div>
                    ) : filteredFolders.length === 0 ? (
                        <p className="permissions-empty-state mb-0">{t("empty")}</p>
                    ) : (
                        <>
                            <Table responsive hover className="mb-3">
                                <thead>
                                    <tr>
                                        <th>{t("table_name")}</th>
                                        <th>{t("table_access")}</th>
                                        <th>{t("table_documents")}</th>
                                        <th className="text-end">{t("table_actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedFolders.map((folder) => (
                                        <tr key={folder.id}>
                                            <td>
                                                <button type="button" className="btn btn-link p-0 fw-semibold text-start org-documents-folder-link" onClick={() => setOpenFolderId(folder.id)}>
                                                    {folder.name}
                                                </button>
                                                {folder.description && <small className="txt-subtle">{folder.description}</small>}
                                                {folder.scope === "employee" && folder.employee_can_upload && (
                                                    <div><small className="txt-subtle">{t("employee_can_upload_badge")}</small></div>
                                                )}
                                                {folder.scope === "employee" && folder.allow_shared_documents && (
                                                    <div><small className="txt-subtle">{t("allow_shared_documents_badge")}</small></div>
                                                )}
                                            </td>
                                            <td className="align-middle org-documents-access">{accessSummary(folder)}</td>
                                            <td className="fw-bold txt-subtle align-middle">{folder.document_count}</td>
                                            <td className="text-end align-middle">
                                                <div className="d-inline-flex gap-2">
                                                    <button className="btn btn-secondary org-documents-row-btn" onClick={() => setEditing(folder)}>{t("edit")}</button>
                                                    <button className="btn btn-outline-danger org-documents-row-btn" onClick={() => handleDelete(folder)}>{t("delete")}</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            <div className="permissions-pagination-wrap">
                                <small className="text-muted">
                                    {t("pagination_summary", {
                                        from: (safePage - 1) * ITEMS_PER_PAGE + 1,
                                        to: Math.min(safePage * ITEMS_PER_PAGE, filteredFolders.length),
                                        total: filteredFolders.length,
                                    })}
                                </small>
                                <Pagination className="mb-0">
                                    <Pagination.Prev disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
                                    <Pagination.Item active>{safePage}</Pagination.Item>
                                    <Pagination.Next disabled={safePage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />
                                </Pagination>
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>
            </>
            )}

            {editing && (
                <FolderModal
                    folder={editing === "new" ? null : editing}
                    defaultScope={scope}
                    grantTargets={grantTargets}
                    onClose={(saved) => {
                        setEditing(null);
                        // Show the new folder even when it was created with a different type than the filter.
                        if (saved) {
                            setScope(saved.scope);
                            setSearch("");
                        }
                    }}
                />
            )}
        </Container>
    );
};

export default OrganizationDocuments;
